type CompanyQrTemplateParams = {
  companyName: string;
  companyLogoUrl?: string | null;
  website: string;
  fileName?: string;
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const roundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
};

export const downloadCompanyQrTemplate = async ({
  companyName,
  companyLogoUrl,
  website,
  fileName,
}: CompanyQrTemplateParams): Promise<void> => {
  const url = (website || '').trim();
  if (!url) throw new Error('Website is required');

  const qrEndpoint = `https://api.qrserver.com/v1/create-qr-code/?size=520x520&margin=0&data=${encodeURIComponent(url)}`;

  const canvas = document.createElement('canvas');
  canvas.width = 1400;
  canvas.height = 2000;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');

  const bg = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  bg.addColorStop(0, '#fffdf8');
  bg.addColorStop(1, '#fff7eb');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  ctx.fillStyle = '#f2d3a4';
  ctx.beginPath();
  ctx.arc(180, 180, 180, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(1220, 1700, 240, 0, Math.PI * 2);
  ctx.fill();

  roundedRect(ctx, 140, 120, 1120, 1760, 44);
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = 'rgba(125, 88, 37, 0.18)';
  ctx.shadowBlur = 28;
  ctx.shadowOffsetY = 12;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  ctx.textAlign = 'center';
  ctx.fillStyle = '#6b3f1d';
  ctx.font = '700 62px Segoe UI, Arial, sans-serif';
  ctx.fillText((companyName || 'Company').slice(0, 42), canvas.width / 2, 250);

  if (companyLogoUrl) {
    try {
      const logo = await loadImage(companyLogoUrl);
      const logoSize = 180;
      const x = (canvas.width - logoSize) / 2;
      const y = 300;
      roundedRect(ctx, x, y, logoSize, logoSize, 28);
      ctx.save();
      ctx.clip();
      ctx.drawImage(logo, x, y, logoSize, logoSize);
      ctx.restore();
    } catch {
      // ignore logo errors
    }
  }

  const qrImage = await loadImage(qrEndpoint);
  const qrSize = 700;
  const qrX = (canvas.width - qrSize) / 2;
  const qrY = 620;
  roundedRect(ctx, qrX - 30, qrY - 30, qrSize + 60, qrSize + 60, 36);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  ctx.strokeStyle = '#f1dfc0';
  ctx.lineWidth = 8;
  ctx.stroke();
  ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

  ctx.fillStyle = '#9b6a36';
  ctx.font = '600 38px Segoe UI, Arial, sans-serif';
  ctx.fillText('Scan to view menu', canvas.width / 2, qrY + qrSize + 100);

  ctx.fillStyle = '#6b3f1d';
  ctx.font = '700 44px Segoe UI, Arial, sans-serif';
  ctx.fillText('architech.al', canvas.width / 2, 1770);

  const outName = (fileName || `${companyName || 'company'}-menu-qr-template`).replace(/[^\w-]+/g, '_');
  const dataUrl = canvas.toDataURL('image/png');
  const anchor = document.createElement('a');
  anchor.href = dataUrl;
  anchor.download = `${outName}.png`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

